import { sha256 } from 'crypto-hash';
import { createScheduledActionArgsTooLargeWarning } from '../Errors';
import type { DelayedAction } from '../api/ReplicantAPI';
import type { ErrorReporter } from '../common/ErrorReporter';
import { ARGS_TOO_LARGE_WARNING_LIMIT_BYTES } from '../common/ReplicantConstants';
import type { default as DB, MetainfoMVCC, ScheduledAction, ScheduledActionKey } from '../db/DB';
import { groupBy } from '../utils/ArrayUtils';
import { mapObject } from '../utils/ObjUtils';
import { duration, unreachable } from '../utils/Utils';

type ActionScheduleUpdate =
    | {
          type: 'scheduleAction';
          actionArgs: unknown;
          actionName: string;
          isSystemAction?: boolean;
          notificationId: string;
          whenToExecute: number;
      }
    | {
          type: 'unscheduleAction';
          isSystemAction?: boolean;
          notificationId: string;
      };

/** Database operations that result from resolving a list of action schedule updates. */
type UpdateOperations = {
    deletedActions: ScheduledActionKey[];
    newActions: [ScheduledActionKey, ScheduledAction][];
    notificationUpdates: {
        [userId: string]: {
            scheduledActionNotifications: {
                [notificationId: string]: ScheduledActionKey | null;
            };
            systemScheduledActionNotifications: {
                [notificationId: string]: ScheduledActionKey | null;
            };
        };
    };
};

/**
 * Given a list of scheduling updates from `api.scheduledActions.schedule`/`.unschedule` calls,
 * resolve the updates with the fewest possible number of database operations.
 */
export async function resolveActionScheduleUpdates(opts: {
    db: DB<any>;
    errorReporter: ErrorReporter;
    now: number;
    scheduleUpdates: (DelayedAction & { type: 'scheduleAction' | 'unscheduleAction' })[];
}): Promise<{ actionsScheduledCount: number }> {
    const updatesByUserId = groupBy(opts.scheduleUpdates, (update) => update.userId);

    const updateOperations: UpdateOperations = {
        deletedActions: [],
        newActions: [],
        notificationUpdates: {},
    };

    await Promise.all(
        Object.values(updatesByUserId).map(async (userUpdates) => {
            const { userId, metainfoMVCC } = userUpdates[0]!;

            const result = await resolveActionScheduleUpdatesPerUser({
                ...opts,
                scheduleUpdates: userUpdates,
                userId,
                metainfoMVCC: metainfoMVCC ?? (await opts.db.metainfoMVCC.fetchMetainfoMVCC(userId)), // `metainfoMVCC` is undefined when scheduling actions for another user
            });

            updateOperations.deletedActions.push(...result.deletedActions);
            updateOperations.newActions.push(...result.newActions);
            updateOperations.notificationUpdates[userId] = result.notificationUpdates[userId]!;
        }),
    );

    await performUpdateOperations(opts.db, updateOperations);

    return { actionsScheduledCount: updateOperations.newActions.length };
}

async function resolveActionScheduleUpdatesPerUser(opts: {
    errorReporter: ErrorReporter;
    now: number;
    scheduleUpdates: ActionScheduleUpdate[];
    userId: string;
    metainfoMVCC: Pick<MetainfoMVCC, 'scheduledActionNotifications' | 'systemScheduledActionNotifications'>;
}): Promise<UpdateOperations> {
    const { errorReporter, metainfoMVCC, now, scheduleUpdates, userId } = opts;

    if (scheduleUpdates.length === 0) {
        return {
            deletedActions: [],
            newActions: [],
            notificationUpdates: {
                [userId]: {
                    scheduledActionNotifications: {},
                    systemScheduledActionNotifications: {},
                },
            },
        };
    }

    const newActionNotifications: { [notificationId: string]: [ScheduledActionKey, ScheduledAction] | null } = {};
    const newSystemActionNotifications: { [notificationId: string]: [ScheduledActionKey, ScheduledAction] | null } = {};

    const oldActionNotifications = metainfoMVCC.scheduledActionNotifications ?? {};
    const oldSystemActionNotifications = metainfoMVCC.systemScheduledActionNotifications ?? {};

    for (const update of scheduleUpdates) {
        const [notifications, oldNotifications] = update.isSystemAction
            ? [newSystemActionNotifications, oldSystemActionNotifications]
            : [newActionNotifications, oldActionNotifications];

        if (update.type === 'scheduleAction') {
            await reportWarningIfArgsAreOversize({ ...update, errorReporter, userId });

            const scheduledActionKey = await getScheduledActionKey(userId, update);

            const scheduledAction: ScheduledAction = {
                userId,
                actionName: update.actionName,
                actionArgs: update.actionArgs,
                notificationId: update.notificationId,
                whenToExecute: update.whenToExecute,
                isSystemAction: update.isSystemAction,
            };

            // Skip scheduling if already scheduled the same action with the same args to the same time slot:
            const previousKey = oldNotifications[update.notificationId];
            const alreadyScheduledToSameTimeslot =
                previousKey?.actionId === scheduledActionKey.actionId &&
                previousKey?.timeslotId === scheduledActionKey.timeslotId;

            if (!alreadyScheduledToSameTimeslot) {
                notifications[update.notificationId] = [scheduledActionKey, scheduledAction];
            }
        } else if (update.type === 'unscheduleAction') {
            const existingKey = oldNotifications[update.notificationId];

            if (existingKey) {
                // If the action is already scheduled in a previous request, unschedule it:
                notifications[update.notificationId] = null;
            } else {
                // If we're scheduling the action in this request, cancel the scheduling:
                delete notifications[update.notificationId];
            }
        } else {
            unreachable(update);
        }
    }

    const keysToDelete: ScheduledActionKey[] = [];
    const newActions: [ScheduledActionKey, ScheduledAction][] = [];

    for (const group of ['normal', 'system']) {
        const [notifications, oldNotifications] =
            group === 'normal'
                ? [newActionNotifications, oldActionNotifications]
                : [newSystemActionNotifications, oldSystemActionNotifications];

        for (const notificationId in notifications) {
            const existingKey = oldNotifications[notificationId];

            if (existingKey) {
                // We are either unscheduling or rescheduling a previously scheduled action: delete the old action
                keysToDelete.push(existingKey);
            }

            const newKey = notifications[notificationId];
            if (newKey) {
                newActions.push(newKey);
            }
        }

        // Clean up old notification IDs from metainfo:
        const oldNotificationIdsToCleanUp = Object.entries(oldNotifications)
            .filter(
                ([notificationId, { timeslotId }]) =>
                    !notifications[notificationId] &&
                    timeslotIdToTimestamp(timeslotId) < now - duration({ minutes: 10 }),
            )
            .map(([notificationId]) => notificationId);

        for (const oldNotificationId of oldNotificationIdsToCleanUp) {
            notifications[oldNotificationId] = null;
        }
    }

    const userNotificationUpdates = {
        scheduledActionNotifications: mapObject(newActionNotifications, (_, val) => (val ? val[0] : null)),
        systemScheduledActionNotifications: mapObject(newSystemActionNotifications, (_, val) => (val ? val[0] : null)),
    };

    return { deletedActions: keysToDelete, newActions, notificationUpdates: { [userId]: userNotificationUpdates } };
}

async function performUpdateOperations(db: DB<any>, updateOperations: UpdateOperations): Promise<void> {
    const { deletedActions, newActions, notificationUpdates } = updateOperations;

    // First delete the old actions that got either unscheduled or rescheduled:
    if (deletedActions.length > 0) {
        await db.scheduledActions.deleteBatch(deletedActions);
    }

    // Then write the newly scheduled actions:
    if (newActions.length > 0) {
        await db.scheduledActions.writeBatch(newActions.map(([key, action]) => ({ key, action })));
    }

    // Finally update notification IDs in user's metainfo item:
    const userNotificationPromises = Object.entries(notificationUpdates).map(
        async ([userId, userNotificationUpdates]) => {
            const hasNotificationUpdates =
                Object.keys(userNotificationUpdates.scheduledActionNotifications).length > 0 ||
                Object.keys(userNotificationUpdates.systemScheduledActionNotifications).length > 0;

            if (hasNotificationUpdates) {
                await db.metainfoMVCC.updateScheduledActionNotifications(userId, userNotificationUpdates);
            }
        },
    );

    await Promise.all(userNotificationPromises);
}

export async function getScheduledActionKey(
    userId: string,
    action: { actionArgs: unknown; notificationId: string; whenToExecute: number; isSystemAction?: boolean },
): Promise<ScheduledActionKey> {
    const timeslotId = timestampToTimeslotId(action.whenToExecute);

    // Using a hash of userId + notificationId + isSystemAction so that multiple actions in the same timeslot are overwritten in db.
    const hashInput =
        userId +
        action.notificationId +
        (action.isSystemAction ? 'system' : 'user') + // Handle scheduling user action with notification ID that clashes with system scheduled actions
        JSON.stringify(action.actionArgs); // Handle rescheduling action on the same time slot with different args
    const truncatedHash = (await sha256(hashInput)).slice(0, 16); // Grab the first 64 bits
    const actionId = BigInt('0x' + truncatedHash).toString();

    return { timeslotId, actionId };
}

export function timestampToTimeslotId(timestamp: number): number {
    return Math.floor(timestamp / 1000 / 60) + 1;
}

function timeslotIdToTimestamp(timeslotId: number): number {
    return timeslotId * duration({ minutes: 1 });
}

async function reportWarningIfArgsAreOversize(opts: {
    actionArgs: unknown;
    actionName: string;
    errorReporter: ErrorReporter;
    userId: string;
}): Promise<void> {
    const { actionArgs, actionName, errorReporter, userId } = opts;

    if (actionArgs) {
        const argsSize = JSON.stringify(actionArgs).length;

        if (argsSize > ARGS_TOO_LARGE_WARNING_LIMIT_BYTES) {
            const warning = createScheduledActionArgsTooLargeWarning({
                argsSize,
                scheduledActionName: actionName,
            });
            await errorReporter.captureException(warning, { user: { id: userId } });
        }
    }
}
